import axios from 'axios';
import { message } from 'antd';
import { API_BASE_URL } from '../config/api';

const request = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

const REQUEST_ID_HEADER = 'X-Request-Id';
let requestSequence = 0;

const generateRequestId = () => {
  requestSequence += 1;
  return `admin_req_${Date.now()}_${requestSequence}`;
};

const readResponseRequestId = (headers = {}) => (
  headers[REQUEST_ID_HEADER] ||
  headers[REQUEST_ID_HEADER.toLowerCase()] ||
  headers['x-request-id']
);

const attachRequestIdToData = (response) => {
  const requestId = readResponseRequestId(response?.headers);
  if (
    requestId &&
    response?.data &&
    typeof response.data === 'object' &&
    !Array.isArray(response.data) &&
    !response.data.requestId
  ) {
    response.data.requestId = requestId;
  }
  return requestId;
};

// Token刷新状态管理
let isRefreshing = false;
let failedQueue = [];

// 处理队列中的请求
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// 判断是否为Token过期错误
const isTokenExpiredError = (status, errorMessage) => {
  // 标准401未授权
  if (status === 401) {
    return true;
  }
  
  // 某些后端实现会返回500但错误信息表明token问题
  if (status === 500 && errorMessage) {
    const tokenErrorPatterns = [
      'Invalid token',
      'invalid token',
      'Token expired',
      'token expired',
      'Token invalid',
      'token invalid',
      'Unauthorized',
      'unauthorized',
      'JWT expired',
      'jwt expired',
      'Authentication failed',
      'authentication failed',
      'Invalid authentication',
      'invalid authentication',
      'jwt malformed',
      'jwt signature is required',
    ];
    
    return tokenErrorPatterns.some(pattern => 
      errorMessage.toLowerCase().includes(pattern.toLowerCase())
    );
  }
  
  return false;
};

/**
 * 认证自身的端点：401 在这里表示「账号口令不对」，不是「会话过期」。
 *
 * 真实故障：登录页输错口令 → 401 → 走 token 刷新分支 → localStorage 里没有
 * refresh token → 抛错 → `window.location.href = '/login'` 整页刷新。
 * 刷新把紧随其后的 message.error 一起冲掉，运营看到的是「页面自己闪了一下、
 * 表单清空、没有任何提示」，完全看不出是口令错了。
 * 这类 401 必须原样 reject，交给调用方（Login.js 的 catch）展示后端 message。
 */
const AUTH_ENDPOINT_PATTERN = /\/(admin\/)?auth\/(login|refresh|logout)\b/;
const isAuthEndpoint = (url) => AUTH_ENDPOINT_PATTERN.test(url || '');

// 刷新token
const refreshToken = async () => {
  const refreshTokenValue = localStorage.getItem('admin_refresh_token');
  
  if (!refreshTokenValue) {
    throw new Error('没有找到刷新令牌');
  }

  try {
    // 使用admin专用的刷新接口
    const response = await axios.post(
      `${API_BASE_URL}/admin/auth/refresh`,
      { refreshToken: refreshTokenValue },
      {
        headers: {
          'Content-Type': 'application/json',
          [REQUEST_ID_HEADER]: generateRequestId(),
        },
      }
    );

    if (response.data.success && response.data.data) {
      const { accessToken, refreshToken: newRefreshToken } = response.data.data;
      
      // 保存新token
      localStorage.setItem('admin_token', accessToken);
      if (newRefreshToken) {
        localStorage.setItem('admin_refresh_token', newRefreshToken);
      }
      
      return accessToken;
    }

    throw new Error(response.data.message || '刷新令牌失败');
  } catch (error) {
    // 刷新失败，清除所有token
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_refresh_token');
    throw error;
  }
};

// 请求拦截器
request.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {};
    if (!config.headers[REQUEST_ID_HEADER]) {
      config.headers[REQUEST_ID_HEADER] = generateRequestId();
    }

    // 如果标记了skipAuth，跳过添加token
    if (config.skipAuth) {
      delete config.skipAuth;
      return config;
    }
    
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
request.interceptors.response.use(
  (response) => {
    attachRequestIdToData(response);
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;
    const responseRequestId = attachRequestIdToData(error.response);
    if (responseRequestId) {
      error.requestId = responseRequestId;
    }

    // 如果请求已经重试过，直接返回错误
    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    if (error.response) {
      const { status, data } = error.response;
      const errorMessage = data?.message || data?.error || error.message || '';

      // 认证端点的 401 是凭据错误，既不刷新也不跳转，直接交回调用方
      if (isAuthEndpoint(originalRequest?.url)) {
        return Promise.reject(error);
      }

      // 检查是否为Token过期错误
      if (isTokenExpiredError(status, errorMessage)) {
        // 如果正在刷新token，将请求加入队列
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return request(originalRequest);
            })
            .catch(err => {
              return Promise.reject(err);
            });
        }

        // 标记正在刷新
        originalRequest._retry = true;
        isRefreshing = true;

        try {
          // 刷新token
          const newToken = await refreshToken();
          
          // 处理队列中的请求
          processQueue(null, newToken);
          
          // 更新原始请求的token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          
          // 重试原始请求
          return request(originalRequest);
        } catch (refreshError) {
          // 刷新失败，处理队列中的请求
          processQueue(refreshError, null);
          
          // 跳转到登录页
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_refresh_token');
          window.location.href = '/login';
          message.error('登录已过期，请重新登录');
          
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      if (originalRequest?.skipErrorMessage) {
        return Promise.reject(error);
      }

      // 其他错误处理
      if (status === 403) {
        // 权限不足 — 不跳转，仅提示
        message.error(data?.message || '权限不足，无法执行此操作');
        return Promise.reject(error);
      } else if (status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_refresh_token');
        window.location.href = '/login';
        message.error('登录已过期，请重新登录');
      } else {
        message.error(data?.message || '请求失败');
      }
    } else {
      message.error('网络错误，请检查网络连接');
    }

    return Promise.reject(error);
  }
);

export default request;
